import { ReactElement } from 'react'
import { Box, Tooltip, useTheme } from '@mui/material'
import { TextTabItem } from '../TextTabs'

export type NRowTabHeaderProps<T extends string> = {
  value: T
  onChange: (next: T) => void
  items: TextTabItem<T>[]
  rows?: number
  showLabels?: boolean
}

const TAB_HEIGHT = 36

export const NRowTabHeader = <T extends string>({
  value,
  onChange,
  items,
  rows = 2,
  showLabels = false,
}: NRowTabHeaderProps<T>): ReactElement => {
  const theme = useTheme()
  const safeRows = Math.max(1, rows)
  const columnCount = Math.ceil(items.length / safeRows)

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        gridAutoRows: `${TAB_HEIGHT}px`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.common.white,
      }}
    >
      {items.map(item => {
        const active = value === item.id
        const compactContent = item.shortIcon ?? item.shortLabel ?? item.label
        return (
          <Tooltip key={item.id} title={showLabels ? '' : item.label} arrow disableInteractive>
            <Box
              component="button"
              type="button"
              data-cy={`element-list-tab-${item.id}`}
              onClick={() => onChange(item.id)}
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                cursor: 'pointer',
                background: 'transparent',
                border: 0,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                fontFamily: 'Roboto',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.2,
                textAlign: 'center',
                color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                backgroundColor: active
                  ? theme.palette.action.selected
                  : theme.palette.common.white,
                boxShadow: active
                  ? `inset 0 -2px 0 ${theme.palette.primary.main}`
                  : 'inset 0 -2px 0 transparent',
                transition: 'background 120ms',
                '&:hover': { background: theme.palette.action.hover },
              }}
            >
              <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {showLabels ? item.label : compactContent}
              </span>
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}
